import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { usePatient } from "@/context/PatientContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { UserCircle, Plus, PencilSimple, Trash, ShareNetwork, UsersThree, SignOut, FileText } from "@phosphor-icons/react";
import { toast } from "sonner";
import ShareDialog from "@/components/ShareDialog";
import { api } from "@/lib/api";

const empty = { name: "", relationship: "", date_of_birth: "", gender: "", blood_type: "", allergies: "", conditions: "", emergency_contact_name: "", emergency_contact_phone: "", notes: "" };

export default function Patients() {
  const { patients, createPatient, updatePatient, deletePatient, setActiveId, activeId, reload } = usePatient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [shareFor, setShareFor] = useState(null);
  const [params, setParams] = useSearchParams();

  useEffect(() => {
    if (params.get("new") === "1") {
      setOpen(true);
      setForm(empty);
      setEditingId(null);
      params.delete("new");
      setParams(params, { replace: true });
    }
  }, [params, setParams]);

  const startEdit = (p) => {
    setEditingId(p.id);
    setForm({ ...empty, ...p });
    setOpen(true);
  };
  const startNew = () => {
    setEditingId(null);
    setForm(empty);
    setOpen(true);
  };
  const save = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await updatePatient(editingId, form);
        toast.success("Patient updated");
      } else {
        await createPatient(form);
        toast.success("Patient added");
      }
      setOpen(false);
    } catch (err) {
      toast.error("Save failed");
    }
  };
  const remove = async (p) => {
    if (!window.confirm(`Remove ${p.name} and all their records?`)) return;
    await deletePatient(p.id);
    toast.success("Patient removed");
  };
  const leave = async (p) => {
    if (!window.confirm(`Stop caring for ${p.name}? You'll lose access.`)) return;
    await api.post(`/patients/${p.id}/leave`);
    toast.success(`You left ${p.name}`);
    reload();
  };

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#576A62] font-semibold">People you care for</div>
          <h1 className="mt-2 font-heading text-3xl sm:text-4xl font-bold">Patients</h1>
        </div>
        <button
          data-testid="patients-add-btn"
          onClick={startNew}
          className="inline-flex items-center gap-2 rounded-full bg-[#2C5545] hover:bg-[#224235] text-white px-5 py-3 font-semibold transition-colors"
        >
          <Plus weight="bold" size={16} /> Add patient
        </button>
      </div>

      {patients.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#EBE8E0] p-12 text-center bg-white">
          <UserCircle size={64} weight="duotone" className="text-[#2C5545] mx-auto" />
          <div className="mt-4 font-heading text-2xl font-bold">No patients yet</div>
          <div className="text-[#576A62] mt-2">Add the first person you're caring for to get started.</div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {patients.map((p) => (
            <div
              key={p.id}
              data-testid={`patient-card-${p.id}`}
              className={`bg-white rounded-2xl border p-6 hover:shadow-md transition-all ${activeId === p.id ? "border-[#2C5545] ring-2 ring-[#2C5545]/20" : "border-[#EBE8E0]"}`}
            >
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-full bg-[#EBE8E0] flex items-center justify-center">
                  <UserCircle size={40} weight="duotone" className="text-[#2C5545]" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-heading text-xl font-bold truncate">{p.name}</div>
                    <RoleBadge role={p.role} />
                  </div>
                  <div className="text-sm text-[#576A62]">
                    {p.relationship || "—"}
                    {p.is_shared && p.shared_by && (
                      <span className="ml-1 text-[#576A62]">· shared by {p.shared_by}</span>
                    )}
                  </div>
                </div>
              </div>
              <dl className="mt-4 text-sm space-y-1.5">
                {p.date_of_birth && <div className="flex gap-2"><dt className="text-[#576A62] w-24">DOB</dt><dd>{p.date_of_birth}</dd></div>}
                {p.blood_type && <div className="flex gap-2"><dt className="text-[#576A62] w-24">Blood</dt><dd>{p.blood_type}</dd></div>}
                {p.allergies && <div className="flex gap-2"><dt className="text-[#576A62] w-24">Allergies</dt><dd className="truncate">{p.allergies}</dd></div>}
                {p.conditions && <div className="flex gap-2"><dt className="text-[#576A62] w-24">Conditions</dt><dd className="truncate">{p.conditions}</dd></div>}
              </dl>
              <div className="mt-5 flex items-center gap-2">
                <button
                  onClick={() => setActiveId(p.id)}
                  data-testid={`patient-select-${p.id}`}
                  className={`flex-1 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${activeId === p.id ? "bg-[#2C5545] text-white" : "border border-[#2C5545] text-[#2C5545] hover:bg-[#EBE8E0]"}`}
                >
                  {activeId === p.id ? "Active" : "Select"}
                </button>
                <Link
                  to={`/app/patients/${p.id}/summary`}
                  data-testid={`patient-summary-${p.id}`}
                  className="p-2 rounded-full border border-[#EBE8E0] hover:bg-[#EBE8E0] text-[#2C5545]"
                  aria-label="Care summary"
                  title="Care summary"
                ><FileText size={18} /></Link>
                {p.role === "owner" && (
                  <button
                    onClick={() => setShareFor(p)}
                    data-testid={`patient-share-${p.id}`}
                    className="p-2 rounded-full border border-[#EBE8E0] hover:bg-[#EBE8E0] text-[#2C5545]"
                    aria-label="Share"
                    title="Share with family"
                  ><ShareNetwork size={18} /></button>
                )}
                {p.role !== "viewer" && (
                  <button
                    onClick={() => startEdit(p)}
                    data-testid={`patient-edit-${p.id}`}
                    className="p-2 rounded-full border border-[#EBE8E0] hover:bg-[#EBE8E0]"
                    aria-label="Edit"
                  ><PencilSimple size={18} /></button>
                )}
                {p.role === "owner" ? (
                  <button
                    onClick={() => remove(p)}
                    data-testid={`patient-delete-${p.id}`}
                    className="p-2 rounded-full border border-[#EBE8E0] text-[#C86A51] hover:bg-[#C86A51]/10"
                    aria-label="Delete"
                  ><Trash size={18} /></button>
                ) : (
                  <button
                    onClick={() => leave(p)}
                    data-testid={`patient-leave-${p.id}`}
                    className="p-2 rounded-full border border-[#EBE8E0] text-[#576A62] hover:bg-[#EBE8E0]"
                    aria-label="Leave"
                    title="Leave this patient"
                  ><SignOut size={18} /></button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">{editingId ? "Edit patient" : "Add patient"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <Field label="Name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="patient-form-name" />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Relationship" value={form.relationship} onChange={(v) => setForm({ ...form, relationship: v })} placeholder="e.g. Mother" testid="patient-form-relationship" />
              <Field label="Date of birth" type="date" value={form.date_of_birth} onChange={(v) => setForm({ ...form, date_of_birth: v })} testid="patient-form-dob" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Gender" value={form.gender} onChange={(v) => setForm({ ...form, gender: v })} testid="patient-form-gender" />
              <Field label="Blood type" value={form.blood_type} onChange={(v) => setForm({ ...form, blood_type: v })} placeholder="e.g. O+" testid="patient-form-blood" />
            </div>
            <Field label="Allergies" value={form.allergies} onChange={(v) => setForm({ ...form, allergies: v })} placeholder="Penicillin, peanuts…" testid="patient-form-allergies" />
            <Field label="Conditions" value={form.conditions} onChange={(v) => setForm({ ...form, conditions: v })} placeholder="Hypertension…" testid="patient-form-conditions" />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Emergency contact name" value={form.emergency_contact_name} onChange={(v) => setForm({ ...form, emergency_contact_name: v })} testid="patient-form-ec-name" />
              <Field label="Emergency contact phone" value={form.emergency_contact_phone} onChange={(v) => setForm({ ...form, emergency_contact_phone: v })} testid="patient-form-ec-phone" />
            </div>
            <Field label="Notes" textarea value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} testid="patient-form-notes" />
            <DialogFooter>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-full px-5 py-2.5 border border-[#EBE8E0] text-[#576A62] hover:bg-[#EBE8E0]/60"
              >Cancel</button>
              <button
                data-testid="patient-form-save"
                type="submit"
                className="rounded-full px-5 py-2.5 bg-[#2C5545] hover:bg-[#224235] text-white font-semibold"
              >{editingId ? "Save changes" : "Add patient"}</button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ShareDialog patient={shareFor} open={!!shareFor} onOpenChange={(o) => !o && setShareFor(null)} />
    </div>
  );
}

function RoleBadge({ role }) {
  if (!role) return null;
  const map = {
    owner: "bg-[#2C5545] text-white",
    editor: "bg-[#3E7B5C]/15 text-[#3E7B5C]",
    viewer: "bg-[#EBE8E0] text-[#576A62]",
  };
  return (
    <span data-testid={`role-badge-${role}`} className={`text-xs uppercase tracking-widest px-2 py-0.5 rounded-full font-semibold ${map[role] || map.viewer}`}>
      {role}
    </span>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, required, testid, textarea }) {
  return (
    <label className="block">
      <div className="text-sm font-medium mb-1.5">{label}{required && <span className="text-[#C86A51]"> *</span>}</div>
      {textarea ? (
        <textarea
          data-testid={testid}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={3}
          className="w-full rounded-xl border border-[#EBE8E0] bg-white px-4 py-3 focus:ring-2 focus:ring-[#2C5545] focus:border-transparent outline-none"
        />
      ) : (
        <input
          data-testid={testid}
          type={type}
          required={required}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full h-11 rounded-xl border border-[#EBE8E0] bg-white px-4 focus:ring-2 focus:ring-[#2C5545] focus:border-transparent outline-none"
        />
      )}
    </label>
  );
}
